import { useState, useEffect } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, ArrowRight } from "lucide-react";
import { motion } from "framer-motion";
import AnimatedLogo from "./AnimatedLogo";

const NAV = [
    { to: "/", label: "Home" },
    { to: "/services", label: "Services" },
    { to: "/task-partners", label: "Task Partners" },
    { to: "/faq", label: "FAQ" },
    { to: "/about", label: "About" },
];

export const Navbar = () => {
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 6);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const linkClass = ({ isActive }) =>
        `relative text-sm font-semibold transition-colors ${
            isActive ? "text-[#F26B1F]" : "text-[#0E2A47]/80 hover:text-[#0E2A47]"
        }`;

    return (
        <header
            data-testid="site-navbar"
            className={`sticky top-0 z-40 transition-all duration-300 ${
                scrolled
                    ? "border-b border-[#ECE4D3] bg-white/85 backdrop-blur-xl shadow-[0_8px_30px_rgba(14,42,71,0.06)]"
                    : "bg-transparent"
            }`}
        >
            <div className="container-atm flex h-24 items-center justify-between md:h-28 lg:h-32">
                <AnimatedLogo
                    size="h-10 sm:h-12 md:h-14"
                    to="/"
                    withWordmark
                    data-testid="nav-logo"
                />

                <nav className="hidden items-center gap-7 lg:flex">
                    {NAV.map((n) => (
                        <NavLink
                            key={n.to}
                            to={n.to}
                            className={linkClass}
                            data-testid={`nav-link-${n.label.toLowerCase()}`}
                        >
                            {({ isActive }) => (
                                <>
                                    {n.label}
                                    {isActive && (
                                        <motion.span
                                            layoutId="nav-underline"
                                            className="absolute -bottom-2 left-0 right-0 h-0.5 rounded-full bg-[#F26B1F]"
                                            transition={{ type: "spring", stiffness: 350, damping: 30 }}
                                        />
                                    )}
                                </>
                            )}
                        </NavLink>
                    ))}
                </nav>

                <div className="hidden lg:block">
                    <Link
                        to="/book"
                        data-testid="nav-book-btn"
                        className="btn-primary !py-3 !px-5 text-sm"
                    >
                        Book Free Consultation <ArrowRight size={16} />
                    </Link>
                </div>

                <button
                    type="button"
                    className="lg:hidden text-[#0E2A47]"
                    onClick={() => setOpen((v) => !v)}
                    aria-label="Toggle menu"
                    data-testid="nav-mobile-toggle"
                >
                    {open ? <X size={24} /> : <Menu size={24} />}
                </button>
            </div>

            {open && (
                <div className="lg:hidden border-t border-[#ECE4D3] bg-white/95 backdrop-blur-xl">
                    <div className="container-atm flex flex-col gap-2 py-4">
                        {NAV.map((n) => (
                            <NavLink
                                key={n.to}
                                to={n.to}
                                onClick={() => setOpen(false)}
                                className={({ isActive }) =>
                                    `rounded-2xl px-4 py-3 text-base font-semibold ${
                                        isActive
                                            ? "bg-[#FDF1E7] text-[#F26B1F]"
                                            : "text-[#0E2A47] hover:bg-[#FAF6EE]"
                                    }`
                                }
                                data-testid={`nav-mobile-${n.label.toLowerCase()}`}
                            >
                                {n.label}
                            </NavLink>
                        ))}
                        <Link
                            to="/book"
                            onClick={() => setOpen(false)}
                            className="btn-primary mt-2 w-full !py-3"
                            data-testid="nav-mobile-book-btn"
                        >
                            Book Free Consultation
                        </Link>
                    </div>
                </div>
            )}
        </header>
    );
};

export default Navbar;
